/**
 * Colours shared by the world (three.js) and the HUD (SVG hexagons): pure data, from the prototype,
 * with the grass a dark green. Indexed by tile kind: stone, soil, grass, water, sapling, fruit sapling, tree, fruit tree, stone
 * hill (reserved), the old wood and stone machines (reserved), ore (dug ground, dark with a violet
 * cast), the infuser in ruins and repaired, the hub (paving round its pad), and the deep layers
 * (rules 19): basalt (dark blue-grey), gold (warm and bright), the core (hot red).
 */
export const TILE_TOP = [
  0xb9c6d6, 0x8e6b48, 0x3e7330, 0x9bcde6, 0x3e7330, 0x3e7330, 0x356629, 0x356629, 0xb8ac9c,
  0xa3a9b3, 0x9ca2ac, 0x5e5870, 0x6f6a66, 0x7d8a93, 0xaab2bc, 0x4a5261, 0xd8ae3c, 0xd0472c,
] as const;

/** Groups (a forest, deep water) look darker and denser. */
export const GROUP_TOP: Readonly<Record<number, number>> = {
  6: 0x2d5823,
  3: 0x7fb3dc,
};

/** The swatch next to a tile's name in lists and labels. */
export const TILE_SWATCH = [
  '#B9C6D6',
  '#8E6B48',
  '#3E7330',
  '#93C8E4',
  '#3E7330',
  '#3E7330',
  '#356629',
  '#356629',
  '#B8AC9C',
  '#A3A9B3',
  '#9CA2AC',
  '#5E5870',
  '#6F6A66',
  '#7D8A93',
  '#AAB2BC',
  '#4A5261',
  '#D8AE3C',
  '#D0472C',
] as const;

export const WATER_DEEP = 0x6eaed6;
export const WATER_DEEPER = 0x4c88c0;
/** Stone above the ground, while the rules have layers. */
export const HIGH_STONE = 0xdce2ea;

/** Crewmates' colours; the server picks one per player. */
export const FIGURE_COLORS = [
  '#E8806C',
  '#6F9FE8',
  '#E9B44C',
  '#A88BE0',
  '#5FB8A0',
  '#E88FB8',
  '#8CC266',
  '#E89A58',
] as const;

export const SPARK_BUILD = 0xf2c14e;
export const SPARK_GROW = 0x8dbf74;
export const SPARK_DIG = 0xb9c6d6;
export const SPARK_FALL = 0xb9c6d6;
/** Water sinking to a lower shore. */
export const SPARK_SINK = 0x7fb3dc;
/** A crystal charge going off: the hot pink of its heart. */
export const SPARK_BLAST = 0xff8fc0;

/** The resources, for the HUD (CSS) and for sparks (hex), by name: wood, stone, crystal. */
export const RESOURCE_COLOR: Readonly<Record<string, string>> = {
  wood: '#B07D4F',
  stone: '#8E97A4',
  crystal: '#9E7FE0',
  'soil-block': '#8E6B48',
  'stone-block': '#B9C6D6',
  'ore-block': '#5E5870',
  'basalt-block': '#4A5261',
  'gold-block': '#D8AE3C',
};
export const RESOURCE_SPARK: Readonly<Record<string, number>> = {
  wood: 0xd1a16b,
  stone: 0xd5dbe3,
  crystal: 0xc2a8f5,
  'soil-block': 0xb08e62,
  'stone-block': 0xdce3ec,
  'ore-block': 0x9e7fe0,
  'basalt-block': 0x8e97a6,
  'gold-block': 0xf4d56a,
};

/** The Build button's warm orange. */
export const BUILD_COLOR = '#FF8F3A';

/**
 * Each type of extractor's colour, by its type in the rules: `ui` for the HUD (its Harvest button
 * when there is something to take, its values), `glow` for the ground it draws from and what lies
 * on it, pulsing on the planet. A new type adds a row.
 */
export const MACHINE_COLOR: Readonly<Record<string, { ui: string; glow: number }>> = {
  crystal: { ui: '#9E7FE0', glow: 0x6b33f2 },
  stone: { ui: '#E0A458', glow: 0xf2952e },
  /** The crystal charges: a hot pink, the energy in their crystals (plan 34). */
  charge: { ui: '#E0609E', glow: 0xe0407f },
  'heavy-charge': { ui: '#C24470', glow: 0xc22f5c },
  /** The infuser: a fresh green, ground made anew (plan 36). */
  infuser: { ui: '#7DBB55', glow: 0x6fae3a },
};

export const hexString = (color: number): string => '#' + color.toString(16).padStart(6, '0');

/** A player's colour from their id, the same on the server and in every app. */
export function colorFor(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return FIGURE_COLORS[h % FIGURE_COLORS.length] ?? '#999999';
}
